import { useCallback, useState } from "react";
import { useTranslation } from "../i18n/useTranslation";
import { safeStorage } from "../../../../shared/lib/safeStorage";

/**
 * The Lura app prompt, per `docs/design/lura-book-v1` BPromo.
 *
 * PRODUCT'S RULE IS THE WHOLE DESIGN OF THIS COMPONENT: the prompt is
 * dismissible and "can never cover or block a booking or management action".
 * That is why it renders ABOVE the primary button inside the footer rather than
 * as an overlay, sheet or interstitial — there is no state in which it sits
 * between a client and the thing they came here to do.
 *
 * It is NOT the same component as the promo on the confirmation screen, and the
 * two should not be merged on the strength of looking alike. This one offers a
 * single destination and points at the app; that one offers account creation as
 * its primary action and changes what it promises depending on whether
 * conversion actually carries the booking. They share four text classes and
 * nothing else.
 */

const DISMISS_KEY = "lura.book.promoDismissed";

/**
 * Storage access, which throws rather than returning null in Safari's private
 * mode (shared/lib/safeStorage absorbs it). A prompt we cannot remember dismissing is a small annoyance; a booking
 * page that throws on load is not, so both directions are guarded.
 */
function readDismissed(): boolean {
  return safeStorage.local.get(DISMISS_KEY) === "1";
}

function writeDismissed(): void {
  // When this fails, dismissal does not persist. It still applies for this
  // visit, which is the part the client actually asked for.
  safeStorage.local.set(DISMISS_KEY, "1");
}

export type PromoDestination = "store" | "app" | null;

interface Props {
  /**
   * Where the button goes. `null` renders NOTHING — no button that goes
   * nowhere, and no empty card around it.
   *
   * Which of "store" and "app" applies depends on whether the app is installed,
   * which the web cannot reliably detect. Stage 7 resolves that against the
   * Universal Link handoff; until then the caller decides.
   */
  dest?: PromoDestination;
  onOpen?: () => void;
}

function Promo({ dest = "app", onOpen }: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(readDismissed);

  const dismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed();
  }, []);

  if (!dest || dismissed) return null;

  return (
    // `complementary` with a name: this is supporting content beside the task,
    // and a screen-reader user gets to know what the region is before deciding
    // whether to enter it.
    <aside
      className="b-promo"
      aria-label={t("promo.region")}
      data-testid="app-promo"
      data-dest={dest}
    >
      {/* Decorative. The heading beside it already names the app, so alt text
          here would be the same words announced twice. */}
      <span className="b-appicon">
        <img src="/icon-192.png" alt="" width={36} height={36} />
      </span>

      <div className="b-promo-b">
        <span className="b-promo-t">{t("promo.title")}</span>
        <span className="b-promo-s">{t("promo.body")}</span>
        <div className="b-promo-a">
          <button
            type="button"
            className="b-btn v-secondary s-sm"
            data-testid="app-promo-open"
            onClick={onOpen}
          >
            {dest === "store" ? t("promo.get") : t("promo.open")}
          </button>
        </div>
      </div>

      {/* A real accessible name. "Dismiss" alone does not say what disappears,
          and this control is next to a button that books an appointment. */}
      <button
        type="button"
        className="b-promo-x"
        data-testid="app-promo-dismiss"
        aria-label={t("promo.dismiss")}
        onClick={dismiss}
      >
        <span aria-hidden="true">×</span>
      </button>
    </aside>
  );
}

export default Promo;
